import { RegionShared } from "@1games-ru/region-shared";
import {
  Equipment,
  EquipmentSlot,
  Inventory,
  InventoryItem,
  QuickSlot,
} from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { merge, omit } from "lodash";
import { RootState } from "@/core/store";

export interface InventoryState {
  quickSlots: (QuickSlot | null)[];
  equipment: Equipment | null;
  inventoryMap: Record<number, Inventory>;
  trade: typeof RegionShared.Backend.Responses.TradeGetStateResponse | null;
  safe: typeof RegionShared.Cef.Requests.InventorySafeOpenRequest | null;
  memoizedItem: InventoryItem | null;
}

const initialState: InventoryState = {
  quickSlots: new Array(6).fill(null),
  equipment: null,
  inventoryMap: {},
  trade: null,
  safe: null,
  memoizedItem: null,
};

const inventorySlice = createSlice({
  name: "inventorySlice",
  initialState,
  reducers: {
    setQuickSlots: (state, action: PayloadAction<InventoryState["quickSlots"]>) => {
      state.quickSlots = action.payload;
    },
    setEquipment: (state, action: PayloadAction<Equipment>) => {
      state.equipment = action.payload;
    },
    setInventory: (state, action: PayloadAction<Inventory>) => {
      state.inventoryMap[action.payload.id] = action.payload;
    },
    setTrade: (
      state,
      action: PayloadAction<typeof RegionShared.Backend.Responses.TradeGetStateResponse>,
    ) => {
      state.trade = action.payload;
    },
    setSafe: (
      state,
      action: PayloadAction<typeof RegionShared.Cef.Requests.InventorySafeOpenRequest | null>,
    ) => {
      state.safe = action.payload;
    },
    changeInventoryItem: (
      state,
      action: PayloadAction<{ inventoryId: number; inventoryItem: Partial<InventoryItem> }>,
    ) => {
      const { inventoryId, inventoryItem } = action.payload;
      const inventory = state.inventoryMap[inventoryId];
      if (inventory) {
        const items = inventory.items || [];
        const index = items.findIndex((item) => item.id === inventoryItem.id);
        if (index > -1) {
          const updatedItem = merge(items[index], inventoryItem);
          items[index] = updatedItem;
          if (state.memoizedItem && state.memoizedItem.id === updatedItem.id) {
            state.memoizedItem = { ...updatedItem };
          }
        }
        state.inventoryMap[inventoryId] = { ...inventory, items };
      }
    },
    addInventoryItem: (
      state,
      action: PayloadAction<{ inventoryId: number; inventoryItem: InventoryItem }>,
    ) => {
      const { inventoryId, inventoryItem } = action.payload;
      const inventory = state.inventoryMap[inventoryId];
      if (inventory) {
        const items = inventory.items || [];
        const index = items.findIndex((item) => item.id === inventoryItem.id);
        if (index === -1) {
          state.inventoryMap[inventoryId] = {
            ...inventory,
            items: [...items, inventoryItem],
          };
        }
      }
    },
    removeInventoryItem: (
      state,
      action: PayloadAction<{ inventoryId: number; inventoryItemId: string }>,
    ) => {
      const { inventoryId, inventoryItemId } = action.payload;
      const inventory = state.inventoryMap[inventoryId];
      if (inventory) {
        const items = inventory.items || [];
        state.inventoryMap[inventoryId] = {
          ...inventory,
          items: items.filter((item) => item.id !== inventoryItemId),
        };
      }
    },
    addEquipItem: (
      state,
      action: PayloadAction<{ equipmentId: number; slotItem: EquipmentSlot }>,
    ) => {
      const { slotItem } = action.payload;
      if (state.equipment) {
        const slots = state.equipment.slots || [];
        const index = slots.findIndex((item) => item.slot === slotItem.slot);

        if (index > -1) {
          state.equipment.slots[index] = { ...slotItem };
        }
      }
    },
    removeEquipItem: (state, action: PayloadAction<{ equipmentId: number; slot: string }>) => {
      const { slot } = action.payload;
      if (state.equipment) {
        const slots = state.equipment.slots || [];
        const index = slots.findIndex((item) => item.slot === slot);
        if (index > -1) {
          state.equipment.slots[index].item = undefined;
        }
      }
    },
    addQuickSlotItem: (state, action: PayloadAction<{ slotItem: QuickSlot }>) => {
      const { slotItem } = action.payload;
      const index = state.quickSlots.findIndex((item) =>
        item?.inventoryItem ? item.inventoryItem.id === slotItem.inventoryItem.id : false,
      );
      if (index > -1) {
        state.quickSlots[index].inventoryItem = undefined;
      }
      state.quickSlots[slotItem.slotNumber - 1] = slotItem;
    },
    removeQuickSlotItem: (state, action: PayloadAction<{ slot: number }>) => {
      const { slot } = action.payload;
      const index = state.quickSlots.findIndex((item) => item.slotNumber === slot);
      if (index > -1) {
        state.quickSlots[index].inventoryItem = undefined;
      }
    },
    setMemoizedItem: (state, action: PayloadAction<InventoryItem | null>) => {
      state.memoizedItem = action.payload;
    },
    deleteInventory: (state, action: PayloadAction<number>) => {
      state.inventoryMap = omit(state.inventoryMap, [`${action.payload}`]);
    },
    reset: (state) => {
      state.equipment = null;
      state.inventoryMap = {};
      state.trade = null;
      state.memoizedItem = null;
      state.quickSlots = [];
      state.safe = null;
    },
  },
});

export const quickSlotsSelector = (state: RootState) => state.inventory.quickSlots;
export const equipmentSelector = (state: RootState) => state.inventory.equipment;
export const inventoryMapSelector = (state: RootState) => state.inventory.inventoryMap;
export const inventoryByIdSelector = createSelector(
  (state: RootState) => state.inventory.inventoryMap,
  (_, inventoryId: number) => inventoryId,
  (inventoryMap: InventoryState["inventoryMap"], inventoryId: number): Inventory | undefined =>
    inventoryMap[inventoryId],
);
export const memoizedItemSelector = (state: RootState) => state.inventory.memoizedItem;
export const tradeSelector = (state: RootState) => state.inventory.trade;
export const safeSelector = (state: RootState) => state.inventory.safe;

export const inventoryReducer = inventorySlice.reducer;
export const {
  setQuickSlots,
  setEquipment,
  setInventory,
  addInventoryItem,
  changeInventoryItem,
  removeInventoryItem,
  addEquipItem,
  removeEquipItem,
  addQuickSlotItem,
  removeQuickSlotItem,
  reset,
  setMemoizedItem,
  deleteInventory,
  setTrade,
  setSafe,
} = inventorySlice.actions;
